// File: src/views/PrivacyPolicy.js

import React from 'react';
import LegalPage from '../components/legal/LegalPage';

function PrivacyPolicy() {
  return (
    <LegalPage title="Privacy Policy" lastUpdated="March 1, 2026">

      <div className="highlight-box">
        <p>
          GreenScience is committed to protecting your privacy. This Privacy
          Policy explains how we collect, use, disclose, and safeguard your
          information when you visit our website.
        </p>
      </div>

      <h2>1. Who We Are</h2>
      <p>
        GreenScience Inc. ("GreenScience", "we", "us", or "our") is a
        technology company headquartered in Toronto, Ontario, Canada. We
        develop AI-powered environmental intelligence solutions for businesses.
        This Privacy Policy applies to information collected through our
        website at{' '}
        <a href="https://www.greenscience.ca">www.greenscience.ca</a>.
      </p>

      <h2>2. Information We Collect</h2>

      <h3>2.1 Information You Provide Directly</h3>
      <p>We collect information you voluntarily provide when you:</p>
      <ul>
        <li>Fill out our contact form (name, email address, company, message)</li>
        <li>Subscribe to our newsletter or updates</li>
        <li>Request a product demonstration</li>
        <li>Communicate with us by email or other means</li>
      </ul>

      <h3>2.2 Information Collected Automatically</h3>
      <p>
        When you visit our Site, we may automatically collect certain technical
        information, including:
      </p>
      <ul>
        <li>IP address and approximate geographic location</li>
        <li>Browser type and version</li>
        <li>Operating system</li>
        <li>Pages visited and time spent on each page</li>
        <li>Referring URLs</li>
        <li>Device identifiers</li>
      </ul>

      <h3>2.3 Cookies and Tracking Technologies</h3>
      <p>
        We use cookies and similar tracking technologies to enhance your
        experience on our Site. Cookies are small data files stored on your
        device. We use:
      </p>
      <ul>
        <li><strong>Essential cookies:</strong> Required for the Site to function properly</li>
        <li><strong>Analytics cookies:</strong> Help us understand how visitors interact with the Site</li>
        <li><strong>Preference cookies:</strong> Remember your settings and preferences</li>
      </ul>
      <p>
        You can control cookies through your browser settings. Note that
        disabling certain cookies may affect Site functionality.
      </p>

      <h2>3. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Respond to your enquiries and provide customer support</li>
        <li>Send you information you have requested about our products and services</li>
        <li>Improve and optimise our Site and user experience</li>
        <li>Analyse usage patterns and trends</li>
        <li>Comply with legal obligations</li>
        <li>Protect against fraudulent or unlawful activity</li>
        <li>Send periodic communications (with your consent where required)</li>
      </ul>

      <h2>4. Legal Basis for Processing</h2>
      <p>
        Where applicable under Canadian privacy law (PIPEDA) and other
        relevant legislation, we process your personal information based on:
      </p>
      <ul>
        <li><strong>Consent:</strong> Where you have given us explicit consent</li>
        <li><strong>Legitimate interests:</strong> To operate and improve our business</li>
        <li><strong>Legal obligation:</strong> To comply with applicable laws</li>
        <li><strong>Contractual necessity:</strong> To fulfil a contract with you</li>
      </ul>

      <h2>5. How We Share Your Information</h2>
      <p>
        We do not sell, trade, or rent your personal information to third
        parties. We may share your information with:
      </p>
      <ul>
        <li>
          <strong>Service providers:</strong> Trusted third-party vendors who
          assist us in operating our Site and conducting our business (e.g.,
          hosting providers, analytics services), subject to confidentiality
          obligations
        </li>
        <li>
          <strong>Legal authorities:</strong> When required by law, court order,
          or governmental authority
        </li>
        <li>
          <strong>Business transfers:</strong> In connection with a merger,
          acquisition, or sale of assets, with appropriate notice to you
        </li>
      </ul>

      <h2>6. Data Retention</h2>
      <p>
        We retain your personal information only for as long as necessary to
        fulfil the purposes outlined in this Privacy Policy, unless a longer
        retention period is required or permitted by law. Contact form
        submissions are typically retained for 24 months.
      </p>

      <h2>7. Data Security</h2>
      <p>
        We implement appropriate technical and organisational measures to
        protect your personal information against unauthorised access,
        alteration, disclosure, or destruction. These measures include
        encryption, secure servers, and access controls.
      </p>
      <p>
        However, no method of transmission over the internet or electronic
        storage is 100% secure. While we strive to use commercially acceptable
        means to protect your information, we cannot guarantee absolute security.
      </p>

      <h2>8. Your Rights</h2>
      <p>
        Depending on your location, you may have the following rights regarding
        your personal information:
      </p>
      <ul>
        <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
        <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
        <li><strong>Deletion:</strong> Request deletion of your personal information</li>
        <li><strong>Withdrawal of consent:</strong> Withdraw consent where processing is based on consent</li>
        <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
        <li><strong>Complaint:</strong> Lodge a complaint with a supervisory authority</li>
      </ul>
      <p>
        To exercise any of these rights, please contact us at{' '}
        <a href="mailto:privacy@greenscience.ca">privacy@greenscience.ca</a>.
        We will respond within 30 days.
      </p>

      <h2>9. International Data Transfers</h2>
      <p>
        Your information may be transferred to and processed in countries other
        than Canada. When we transfer personal information internationally, we
        ensure appropriate safeguards are in place in accordance with applicable
        privacy laws.
      </p>

      <h2>10. Children's Privacy</h2>
      <p>
        Our Site is not directed to individuals under the age of 16. We do not
        knowingly collect personal information from children. If you believe we
        have inadvertently collected information from a child, please contact us
        immediately and we will take steps to delete such information.
      </p>

      <h2>11. Third-Party Links</h2>
      <p>
        Our Site may contain links to third-party websites. This Privacy Policy
        does not apply to those sites. We encourage you to review the privacy
        policies of any third-party sites you visit.
      </p>

      <h2>12. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you
        of any significant changes by posting the new policy on this page with
        an updated "Last updated" date. We encourage you to review this policy
        periodically.
      </p>

      <h2>13. Contact Us</h2>
      <p>
        If you have any questions, concerns, or requests regarding this Privacy
        Policy or our data practices, please contact our Privacy Officer:
      </p>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:privacy@greenscience.ca">privacy@greenscience.ca</a></li>
        <li><strong>Address:</strong> GreenScience Inc., Toronto, Ontario, Canada</li>
        <li><strong>Web:</strong> <a href="/contact">Contact Form</a></li>
      </ul>

    </LegalPage>
  );
}

export default PrivacyPolicy;